import CloseIcon from '@mui/icons-material/Close';
import { Box, Divider, Fade, IconButton, Modal, Stack } from '@mui/material';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { useToastNotificationContext } from '../../../hooks/global/alert/useToastNotification';
import AudioPlayer from '../../reportSubmission/voiceReport/audioPlayer';

interface ViewVoiceReportTranscriptProps {
  open: boolean;
  onClose: () => void;
  audioURL: string;
  transcriptURL: string;
  title: string;
}

interface AWSTranscript {
  results: {
    transcripts: { transcript: string }[];
  };
}

function ViewVoiceReportTranscript({
  open,
  onClose,
  transcriptURL,
  audioURL,
  title,
}: ViewVoiceReportTranscriptProps) {
  const intl = useIntl();
  const { createToastNotification } = useToastNotificationContext();
  const [transcript, setTranscript] = useState<string>('');

  useEffect(() => {
    (async () => {
      if (!transcriptURL) {
        return;
      }

      try {
        const downloadedTranscript = await axios
          .create({
            baseURL: transcriptURL,
          })
          .get('', {
            responseType: 'blob',
            headers: {
              Accept: 'application/octet-stream',
            },
          });
        const fileReader = new FileReader();
        fileReader.addEventListener(
          'load',
          () => {
            const result = fileReader.result;
            if (typeof result === 'string') {
              const parsedTranscipt = JSON.parse(result) as AWSTranscript;
              setTranscript(parsedTranscipt.results.transcripts[0].transcript);
            }
          },
          false,
        );
        fileReader.readAsText(downloadedTranscript.data);
      } catch (error) {
        createToastNotification(
          intl.formatMessage({ defaultMessage: 'Something went wrong' }),
          'error',
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcriptURL]);

  if (!transcriptURL) {
    return null;
  }

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition>
      <Fade in={open}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '70vw',
            height: '80vh',
            padding: '20px',
            borderRadius: '12px',
            background: ' #FFF',
          }}
        >
          <Stack width='100%' height='100%'>
            <Stack direction='row' gap={2} justifyContent='space-between'>
              <Stack justifyContent='center'>{title}</Stack>
              <Stack justifyContent='center'>
                <IconButton onClick={onClose}>
                  <CloseIcon />
                </IconButton>
              </Stack>
            </Stack>
            <Stack
              direction={{
                xs: 'column',
                lg: 'row',
              }}
              gap={2}
            >
              <Stack
                flexGrow={1}
                justifyContent='center'
                paddingY={1}
                paddingX={2}
              >
                <AudioPlayer audioURL={audioURL} noMargin />
              </Stack>
            </Stack>
            <Divider />
            <Stack gap={2.5} overflow='scroll' paddingTop={2}>
              {transcript}
            </Stack>
          </Stack>
        </Box>
      </Fade>
    </Modal>
  );
}

export default ViewVoiceReportTranscript;
